/* eslint-disable no-console */
import * as React from 'react'
import useFetch, { CachePolicies } from 'use-http'
import { MLSortDirection, MLSortParams } from '../constants'
import { useDebounce } from '../hooks/useDebounce'

interface UserPreferencesContextValue {
  prefs: UserPreferences
  setPreviewVisible(visible: boolean): void
  setLastTab(key: TabKey): void
  setSortBy(sortBy: MLSortParams): void
  setSortDirection(direction: MLSortDirection): void
  setCollectionsSortBy(sortBy: CollectionsSortParam): void
  setCollectionsSortDirection(direction: MLSortDirection): void
}

const DEFAULT_PREFS: UserPreferences = {
  previewVisible: false,
  lastTab: 'summary',
  sortBy: MLSortParams.CREATED,
  sortDirection: MLSortDirection.DESC,
  collectionsSortBy: MLSortParams.CREATED,
  collectionsSortDirection: MLSortDirection.DESC,
}

const TAB_KEYS: TabKey[] = [
  'summary',
  'customMetadata',
  'history',
  'comments',
]

const SORT_PARAMS = Object.values(MLSortParams)
const SORT_DIRECTIONS = Object.values(MLSortDirection)
const COLLECTIONS_SORT_PARAMS: CollectionsSortParam[] = [
  MLSortParams.CREATED,
  MLSortParams.NAME,
  MLSortParams.RELEVANCE,
]

const USER_PREFS_CACHE_PREFIX = 'ik:user-preferences:'

function cacheKey(userId: string): string {
  return `${USER_PREFS_CACHE_PREFIX}${userId}`
}

function isTabKey(value: unknown): value is TabKey {
  return TAB_KEYS.includes(value as TabKey)
}

function isSortParam(value: unknown): value is MLSortParams {
  return SORT_PARAMS.includes(value as MLSortParams)
}

function isSortDirection(value: unknown): value is MLSortDirection {
  return SORT_DIRECTIONS.includes(value as MLSortDirection)
}

// eslint-disable-next-line import/exports-last
export function isCollectionsSortParam(
  value: unknown,
): value is CollectionsSortParam {
  return COLLECTIONS_SORT_PARAMS.includes(value as CollectionsSortParam)
}

function fromApiPrefs(
  api: Partial<UserPreferences> | null | undefined,
): UserPreferences {
  if (!api) {
    return DEFAULT_PREFS
  }
  return {
    previewVisible: api.previewVisible ?? DEFAULT_PREFS.previewVisible,
    lastTab: isTabKey(api.lastTab) ? api.lastTab : DEFAULT_PREFS.lastTab,
    sortBy: isSortParam(api.sortBy) ? api.sortBy : DEFAULT_PREFS.sortBy,
    sortDirection: isSortDirection(api.sortDirection)
      ? api.sortDirection
      : DEFAULT_PREFS.sortDirection,
    collectionsSortBy: isCollectionsSortParam(api.collectionsSortBy)
      ? api.collectionsSortBy
      : DEFAULT_PREFS.collectionsSortBy,
    collectionsSortDirection: isSortDirection(api.collectionsSortDirection)
      ? api.collectionsSortDirection
      : DEFAULT_PREFS.collectionsSortDirection,
  }
}

function prefsEqual(a: UserPreferences, b: UserPreferences): boolean {
  return (
    a.previewVisible === b.previewVisible &&
    a.lastTab === b.lastTab &&
    a.sortBy === b.sortBy &&
    a.sortDirection === b.sortDirection &&
    a.collectionsSortBy === b.collectionsSortBy &&
    a.collectionsSortDirection === b.collectionsSortDirection
  )
}

function readCachedPrefs(userId: string | undefined): UserPreferences | null {
  if (!userId) {
    return null
  }
  try {
    const raw = window.localStorage.getItem(cacheKey(userId))
    if (!raw) {
      return null
    }
    return fromApiPrefs(JSON.parse(raw) as Partial<UserPreferences>)
  } catch {
    return null
  }
}

function writeCachedPrefs(userId: string, prefs: UserPreferences): void {
  try {
    window.localStorage.setItem(cacheKey(userId), JSON.stringify(prefs))
  } catch {
    console.error('Error writing cached preferences', userId, prefs)
  }
}

// eslint-disable-next-line import/exports-last
export function clearCachedUserPreferences(): void {
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i)
      if (key?.startsWith(USER_PREFS_CACHE_PREFIX)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => void window.localStorage.removeItem(key))
  } catch {
    console.error('Error clearing cached preferences')
  }
}

interface UserPreferencesProviderProps {
  userId: string | undefined
  children: React.ReactNode
}

export const UserPreferencesProvider: React.FC<
  UserPreferencesProviderProps
> = ({ userId, children }) => {
  const [prefs, setPrefs] = React.useState<UserPreferences>(
    () => readCachedPrefs(userId) ?? DEFAULT_PREFS,
  )
  const debouncedPrefs = useDebounce(prefs, 500)
  // Only true after the user explicitly changes a preference — prevents
  // PATCHing on initial mount or after loading server prefs into state.
  const hasUserChangedPrefs = React.useRef(false)
  const hasHydratedFromApi = React.useRef(false)
  const editedKeys = React.useRef(new Set<keyof UserPreferences>())
  const pendingKeys = React.useRef(new Set<keyof UserPreferences>())

  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  // Initial load from API
  React.useEffect(() => {
    let cancelled = false

    async function load(): Promise<void> {
      try {
        const data = (await request.get(
          `/api/user/${userId}/preferences`,
        )) as UserPreferences | null

        if (cancelled) {
          return
        }

        const next = fromApiPrefs(data)
        hasHydratedFromApi.current = true
        setPrefs(curr => {
          const merged: UserPreferences = { ...next }
          editedKeys.current.forEach(key => {
            Object.assign(merged, { [key]: curr[key] })
          })
          return prefsEqual(curr, merged) ? curr : merged
        })
      } catch {
        // On error, keep cached or default prefs already in state
      }
    }

    hasUserChangedPrefs.current = false
    hasHydratedFromApi.current = false
    editedKeys.current.clear()
    pendingKeys.current.clear()

    if (userId) {
      const cached = readCachedPrefs(userId) ?? DEFAULT_PREFS
      setPrefs(curr => (prefsEqual(curr, cached) ? curr : cached))
      void load()
    } else {
      setPrefs(curr => (prefsEqual(curr, DEFAULT_PREFS) ? curr : DEFAULT_PREFS))
    }

    return () => {
      cancelled = true
    }
  }, [userId])

  React.useEffect(() => {
    if (!userId) {
      return
    }
    // Only persist after the first GET (or a user edit) so logout/switch
    // does not get a default cache written before the API responds.
    if (!hasHydratedFromApi.current && !hasUserChangedPrefs.current) {
      return
    }
    writeCachedPrefs(userId, prefs)
  }, [prefs, userId])

  // Debounced sync to server — only runs after a user-initiated change
  React.useEffect(() => {
    if (!userId || pendingKeys.current.size === 0) {
      return
    }

    const body: Record<string, unknown> = {}
    pendingKeys.current.forEach(key => {
      body[key] = debouncedPrefs[key]
    })
    pendingKeys.current.clear()

    void request.patch(`/api/user/${userId}/preferences`, body)
  }, [debouncedPrefs, userId])

  const setPreviewVisible = React.useCallback((visible: boolean) => {
    hasUserChangedPrefs.current = true
    editedKeys.current.add('previewVisible')
    pendingKeys.current.add('previewVisible')
    setPrefs(curr => ({
      ...curr,
      previewVisible: visible,
    }))
  }, [])

  const setLastTab = React.useCallback((key: TabKey) => {
    hasUserChangedPrefs.current = true
    editedKeys.current.add('lastTab')
    pendingKeys.current.add('lastTab')
    setPrefs(curr => ({
      ...curr,
      lastTab: key,
    }))
  }, [])

  const setSortBy = React.useCallback((sortBy: MLSortParams) => {
    hasUserChangedPrefs.current = true
    editedKeys.current.add('sortBy')
    pendingKeys.current.add('sortBy')
    setPrefs(curr => ({
      ...curr,
      sortBy,
    }))
  }, [])

  const setSortDirection = React.useCallback((direction: MLSortDirection) => {
    hasUserChangedPrefs.current = true
    editedKeys.current.add('sortDirection')
    pendingKeys.current.add('sortDirection')
    setPrefs(curr => ({
      ...curr,
      sortDirection: direction,
    }))
  }, [])

  const setCollectionsSortBy = React.useCallback(
    (sortBy: CollectionsSortParam) => {
      hasUserChangedPrefs.current = true
      editedKeys.current.add('collectionsSortBy')
      pendingKeys.current.add('collectionsSortBy')
      setPrefs(curr => ({
        ...curr,
        collectionsSortBy: sortBy,
      }))
    },
    [],
  )

  const setCollectionsSortDirection = React.useCallback(
    (direction: MLSortDirection) => {
      hasUserChangedPrefs.current = true
      editedKeys.current.add('collectionsSortDirection')
      pendingKeys.current.add('collectionsSortDirection')
      setPrefs(curr => ({
        ...curr,
        collectionsSortDirection: direction,
      }))
    },
    [],
  )

  const value = React.useMemo(
    () => ({
      prefs,
      setPreviewVisible,
      setLastTab,
      setSortBy,
      setSortDirection,
      setCollectionsSortBy,
      setCollectionsSortDirection,
    }),
    [
      prefs,
      setPreviewVisible,
      setLastTab,
      setSortBy,
      setSortDirection,
      setCollectionsSortBy,
      setCollectionsSortDirection,
    ],
  )

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  )
}

export function useUserPreferences(): UserPreferencesContextValue {
  const context = React.useContext(UserPreferencesContext)
  if (!context) {
    throw new Error(
      'useUserPreferences must be used within UserPreferencesProvider',
    )
  }
  return context
}

export type TabKey = 'summary' | 'customMetadata' | 'history' | 'comments'

export type CollectionsSortParam =
  | MLSortParams.CREATED
  | MLSortParams.NAME
  | MLSortParams.RELEVANCE

export interface UserPreferences {
  previewVisible: boolean
  lastTab: TabKey
  sortBy: MLSortParams
  sortDirection: MLSortDirection
  collectionsSortBy: CollectionsSortParam
  collectionsSortDirection: MLSortDirection
}

export const UserPreferencesContext =
  React.createContext<UserPreferencesContextValue | null>(null)
